import React, { useState } from 'react';
import { View } from 'react-native';
import type { ImportResult } from '@hrms/shared';
import { api } from '../../../lib/api';
import { openFile } from '../../../lib/files';
import { useMut } from '../../../lib/query';
import type { UploadFile } from '../../../lib/upload';
import { appendFile } from '../../../lib/upload';
import { useGo } from '../../../navigation/nav';
import { pickSpreadsheet, SPREADSHEET_SUPPORTED } from '../components/spreadsheetPicker';
import { Alert, Button, Card, DataTable, Grid, Icon, PageHeader, Row, Screen, Stack, StatCard, Stepper, Text, radius, useTheme, useToast } from '../../../ui';

/** CSV / XLSX employee import: pick → dry run (validation report) → commit. */
export function BulkImportScreen() {
  const { colors } = useTheme();
  const go = useGo();
  const toast = useToast();
  const [file, setFile] = useState<UploadFile | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [done, setDone] = useState<ImportResult | null>(null);
  const fd = () => {
    const f = new FormData();
    appendFile(f, 'file', file);
    return f;
  };
  const dry = useMut(() => api.employees.import(fd(), true), { onSuccess: (r) => setResult(r) });
  const commit = useMut(() => api.employees.import(fd(), false), {
    invalidate: [['employees'], ['dashboard']],
    success: (r) => `${r.created} employees imported`,
    onSuccess: (r) => setDone(r),
  });
  const step = done ? 3 : result ? 2 : file ? 1 : 0;
  const reset = () => {
    setFile(null);
    setResult(null);
    setDone(null);
  };
  const choose = async () => {
    const f = await pickSpreadsheet();
    if (f) {
      setFile(f);
      setResult(null);
      setDone(null);
    }
  };
  return (
    <Screen testID="bulk-import">
      <PageHeader
        title="Bulk import employees"
        subtitle="Upload a CSV or XLSX. Every row is validated before anything is saved."
        actions={<Button icon="download" title="Download template" onPress={() => void openFile('/employees/import/template').catch(() => toast.error('Could not download the template'))} testID="import-template" />}
      />
      <Stepper steps={['Upload file', 'Validate (dry run)', 'Review', 'Imported']} current={step} />
      {!SPREADSHEET_SUPPORTED && <Alert tone="info">Picking spreadsheets needs the web app. Open HRMS in a browser to import employees; the template can still be downloaded here.</Alert>}
      <Card>
        <View style={{ borderWidth: 2, borderStyle: 'dashed', borderColor: colors.borderStrong, borderRadius: radius.lg, padding: 28, alignItems: 'center', gap: 10, backgroundColor: colors.surface2 }}>
          <Icon name="upload" size={28} color={colors.muted} />
          <Text weight={file ? 'semibold' : 'regular'} align="center">
            {file ? file.name : 'Choose a .csv or .xlsx file'}
          </Text>
          {file?.size ? <Text variant="small">{(file.size / 1024).toFixed(1)} KB</Text> : null}
          <Row justify="center">
            <Button title={file ? 'Choose another' : 'Browse files'} icon="file" disabled={!SPREADSHEET_SUPPORTED} onPress={choose} testID="import-choose" />
            {file && <Button variant="primary" icon="checkCircle" title="Validate" loading={dry.isPending} onPress={() => dry.mutate(undefined)} testID="import-validate" />}
          </Row>
        </View>
      </Card>
      {result && !done && (
        <Stack>
          <Grid min={160}>
            <StatCard label="Rows" value={result.total} icon="list" />
            <StatCard label="Valid" value={result.valid} icon="check" tone="success" />
            <StatCard label="With errors" value={result.errors.length} icon="alert" tone={result.errors.length ? 'danger' : 'neutral'} />
          </Grid>
          {result.errors.length ? (
            <Card title="Validation report" subtitle="Fix these rows in your file and re-upload, or import only the valid rows.">
              <DataTable
                rows={result.errors}
                keyExtractor={(r) => r.row}
                testID="import-errors"
                columns={[
                  { key: 'row', title: 'Row', width: 70, flex: 0 },
                  {
                    key: 'messages',
                    title: 'Problems',
                    width: 280,
                    flex: 4,
                    sortable: false,
                    render: (r) => (
                      <View style={{ gap: 2 }}>
                        {r.messages.map((m) => (
                          <Text key={m} color={colors.danger}>
                            • {m}
                          </Text>
                        ))}
                      </View>
                    ),
                  },
                ]}
              />
            </Card>
          ) : (
            <Alert tone="success">{`All ${result.valid} rows are valid.`}</Alert>
          )}
          <Row justify="flex-end">
            <Button title="Start over" onPress={reset} />
            <Button variant="primary" disabled={!result.valid} loading={commit.isPending} title={`Import ${result.valid} valid row${result.valid === 1 ? '' : 's'}`} onPress={() => commit.mutate(undefined)} testID="import-commit" />
          </Row>
        </Stack>
      )}
      {done && (
        <Alert tone="success" title={`${done.created} employees created`}>
          <Text color={colors.success}>{done.errors.length ? `${done.errors.length} rows were skipped.` : 'No rows were skipped.'}</Text>
          <Row style={{ marginTop: 8 }}>
            <Button size="sm" title="View employees" onPress={() => go('EmployeeList')} />
            <Button size="sm" variant="ghost" title="Import another file" onPress={reset} />
          </Row>
        </Alert>
      )}
    </Screen>
  );
}
